<template>
  <div
    class="relative flex-1 overflow-auto rounded-md border border-slate-300 dark:border-slate-300 dark:bg-slate-700 dark:text-gray-300"
  >
    <table class="dashboard-table" style="border-spacing: 0">
      <thead>
        <draggable
          v-model="localColumns"
          item-key="name"
          tag="tr"
          :component-data="{ as: 'transition-group' }"
          :revert-on-spill="false"
          :draggable="'.draggable-cell'"
          :move="handleMove"
          @update="handleDrag"
        >
          <template #item="{ index }">
            <DashBoardHeadColumnComponent
              v-model:column="columns[index]"
              v-model:sort="localSort"
              v-model:cell-width="cellWidth"
              v-model:require-save-dashboard="localRequireSaveDashboard"
              :index="index"
              :is-last="columns.length === index + 1"
              :resize-able="columns.length > 1"
              :search-able="true"
              :available-column-fields="columnTypes"
              @delete:column="handleDeleteColumns"
              @update:dashboard="updateDashboard"
            />
          </template>
        </draggable>
      </thead>
      <tbody>
        <!-- the header stays: the columns and the sort can be changed on an empty list -->
        <tr v-if="!commissionNotes.length">
          <!-- as the list of the invoices: a spinner while the list is on its way, the message once it came empty -->
          <td
            v-if="loading"
            :colspan="columns.length"
            class="p-6"
            data-test="loading"
          >
            <div class="flex items-center justify-center">
              <LoadingComponent size="small" />
            </div>
          </td>
          <td
            v-else
            :colspan="columns.length"
            class="p-6"
            data-test="no-results"
          >
            <div class="flex items-center justify-center">
              <h3
                class="rounded border border-primary bg-primary-100 px-2 py-0.5 text-base font-bold"
              >
                {{ $t('global.no_results') }}
              </h3>
            </div>
          </td>
        </tr>
        <tr
          v-for="commissionNote in paginatedCommissionNotes"
          :key="commissionNote.id"
        >
          <td
            v-for="(column, index) in columns"
            :key="`td-${commissionNote.id}-${index}`"
            class="min-w-[50px] border-b border-slate-300 p-0 dark:border-slate-500"
            :style="{ width: cellWidth[index] }"
          >
            <CommissionNoteCell
              :column="column"
              :commission-note="commissionNote"
            />
          </td>
        </tr>
        <tr v-if="commissionNotes.length > page * pageSize">
          <td class="pagination" :colspan="columns.length">
            <button
              class="flex w-full cursor-pointer flex-row items-center justify-center gap-1 bg-secondary px-1 py-2 text-xs font-semibold text-contrast-secondary"
              @click="page++"
            >
              <span>{{ $t('global.display_more') }}</span>
              <span>{{ page * pageSize }}/{{ commissionNotes.length }}</span>
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import { mapGetters } from 'vuex'
import DashBoardHeadColumnComponent from '@c/Dashboard/Content/Dossier/components/Head/DashBoardHeadColumnComponent'
import CommissionNoteCell from '@c/Dashboard/Content/CommissionNote/CommissionNoteCell.vue'
import LoadingComponent from '@c/TheLoadingComponent.vue'

export default {
  name: 'CommissionNoteTable',
  components: {
    CommissionNoteCell,
    DashBoardHeadColumnComponent,
    LoadingComponent,
    draggable
  },
  props: {
    commissionNotes: { type: Array, required: true },
    columns: { type: Array, required: true },
    sort: { type: Object, required: false, default: () => ({}) },
    requireSaveDashboard: { type: Boolean, required: false, default: false },
    /** The list is on its way: an empty list is not a list without result yet */
    loading: { type: Boolean, required: false, default: false }
  },
  emits: [
    'update:columns',
    'update:require-save-dashboard',
    'update:sort',
    'update:dashboard'
  ],
  data() {
    const cellWidth = {}
    this.columns.forEach((column, key) => {
      cellWidth[key] = column.visual?.width || 'auto'
    })
    return { cellWidth, pageSize: 100, page: 1 }
  },
  computed: {
    ...mapGetters({
      columnTypes: 'metadata/getColumnTypesCommissionNoteDashboard'
    }),
    paginatedCommissionNotes() {
      return this.commissionNotes.slice(0, this.pageSize * this.page)
    },
    localRequireSaveDashboard: {
      get() {
        return this.requireSaveDashboard
      },
      set(value) {
        this.$emit('update:require-save-dashboard', value)
      }
    },
    localColumns: {
      get() {
        return this.columns ?? []
      },
      set(value) {
        this.$emit('update:columns', value)
      }
    },
    localSort: {
      get() {
        return this.sort
      },
      set(value) {
        this.$emit('update:sort', value)
      }
    }
  },
  watch: {
    // another list (search, filter, sort) starts from its first page; the same notes
    // in the same order (a column moved, one resized) keep the page the user is on
    commissionNotes(list, previous) {
      if (
        list.length !== previous.length ||
        list.some((note, index) => note.id !== previous[index].id)
      ) {
        this.page = 1
      }
    }
  },
  methods: {
    handleDrag() {
      this.localRequireSaveDashboard = true
    },
    handleMove(e) {
      return e.related.previousSibling !== null
    },
    handleDeleteColumns(idx) {
      this.columns.splice(idx, 1)
      this.updateDashboard(false)
    },
    updateDashboard(value) {
      this.$emit('update:dashboard', value)
    }
  }
}
</script>
